import React, { useState, useRef, useEffect } from "react";
import Papa from "papaparse";
import "../../../styles/pages/AdhocInvestigation/CollapsibleCSVTable.scss";

const CollapsibleCSVTable = ({ data, title = "Expand Data" }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  let csv = null;
  if (typeof data === "string") {
    const parsed = Papa.parse(data, { skipEmptyLines: true });
    csv = { headers: parsed.data[0] || [], rows: parsed.data.slice(1) };
  } else {
    csv = { headers: [], rows: [] };
  }

  // Row limiting
  const DEFAULT_ROW_LIMIT = 25;
  const [rowLimit, setRowLimit] = useState(DEFAULT_ROW_LIMIT);
  const showMoreRows = () => setRowLimit(lim => Math.min(lim + DEFAULT_ROW_LIMIT, csv.rows.length));

  // Cell expansion state
  const [expandedCells, setExpandedCells] = useState({});
  const handleExpandCell = (rowIdx, colIdx) => {
    setExpandedCells(prev => ({ ...prev, [`${rowIdx},${colIdx}`]: true }));
  };

  // For dual scrollbars (top and bottom horizontal)
  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const tableRef = useRef(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  useEffect(() => {
    if (tableRef.current) {
      setTableScrollWidth(tableRef.current.scrollWidth);
    }
  }, [csv]);
  const syncScroll = (source, target) => {
    if (target.current && source.current) {
      target.current.scrollLeft = source.current.scrollLeft;
    }
  };

  return (
    <div className="collapsible-csv-table">
      <button 
        className="view-data-button"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? "Hide Data" : title}
      </button>
      {isExpanded && (
        <>
          <div
            ref={topScrollRef}
            className="csv-table-scrollbar csv-table-scrollbar--top"
            onScroll={() => syncScroll(topScrollRef, bottomScrollRef)}
          >
            <div style={{ width: tableScrollWidth || '100%', height: 1 }} />
          </div>
          <div
            className="csv-table-scrollbar csv-table-wrapper"
            ref={bottomScrollRef}
            onScroll={() => syncScroll(bottomScrollRef, topScrollRef)}
          >
            <table className="csv-table" ref={tableRef}>
              <thead>
                <tr>
                  {csv.headers.map((h, i) => {
                    const wideHeaders = ["body", "events", "malware", "otherCases"];
                    return (
                      <th key={i} className={`csv-table-header${wideHeaders.includes(h) ? ' csv-table-header--wide' : ''}`}>{h}</th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {csv.rows.slice(0, rowLimit).map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => {
                      const key = `${i},${j}`;
                      const isExpanded = expandedCells[key];
                      const isLong = typeof cell === 'string' && cell.length > 300;
                      const wideHeaders = ["body", "events", "malware", "otherCases"];
                      return (
                        <td key={j} className={`csv-table-cell${wideHeaders.includes(csv.headers[j]) ? ' csv-table-cell--wide' : ''}`}>
                          <div className="csv-table-cell-content">
                            {isLong && !isExpanded ? (
                              <>
                                {cell.slice(0, 300)}...{' '}
                                <button className="csv-table-show-more" onClick={() => handleExpandCell(i, j)}>Show more</button>
                              </>
                            ) : (
                              cell
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="csv-table-show-more-container">
              <button className="csv-table-show-more" onClick={showMoreRows}>
                Show 25 more rows ({Math.max(0, csv.rows.length - (rowLimit + 25))} remaining)
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CollapsibleCSVTable; 